import React from 'react';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { Session } from 'next-auth';
import { Global, css } from '@emotion/react';
import Script from 'next/script';
import GreenlightScript from '!!raw-loader!../context/Optimizely/script.min';

import { addRumContext, initializeRum } from '../utils/datadogRum';
import { initializeMixpanel } from '../utils/mixpanel';

import { AuthProvider } from '../context/Auth';
import { UserProvider } from '../context/User';
import { PageDataProvider } from '../context/PageData';
import { OptimizelyProvider } from '../context/Optimizely';
import { SubscriptionsProvider } from '../context/Subscriptions';

import { ThemeProvider } from '../theme';
import { GreenlightTheme } from '../theme/themes/greenlight';

import { glNormalize } from '../components/Metadata/gl-normalize.styles';

import '@/styles/global.css';
import '@/fonts/style.css';
import '@/styles/normalize.css';
import '@/styles/bright-edge.css';
import '@/styles/outgrow.css';
import useSOCRadar from '~/utils/socRadar';

initializeRum();

// Wait for Transcend consent before initializing Mixpanel
if (typeof window !== 'undefined' && window.airgap) {
  const initAfterConsent = async () => {
    try {
      if (typeof window.airgap?.ready === 'function') {
        window.airgap.ready(async (airgap: any) => {
          if (typeof airgap.sync === 'function') {
            await airgap.sync();
          }
          if (typeof airgap.getConsent === 'function') {
            const consent = airgap.getConsent();
            const hasConsent =
              consent?.confirmed === true ||
              (consent?.purposes &&
                Object.values(consent.purposes).some((p: any) => p === true));
            if (hasConsent) {
              console.log('Consent confirmed, initializing Mixpanel...');
              initializeMixpanel();
            } else {
              console.log('No consent yet, Mixpanel will wait...');
              // Listen for consent changes
              if (typeof airgap.onConsentChange === 'function') {
                airgap.onConsentChange(() => {
                  console.log('Consent changed, initializing Mixpanel...');
                  initializeMixpanel();
                });
              }
            }
          } else {
            // If we can't check consent, initialize anyway after a delay
            setTimeout(() => initializeMixpanel(), 2000);
          }
        });
      } else {
        setTimeout(() => initializeMixpanel(), 2000);
      }
    } catch (error) {
      console.warn('Error waiting for Transcend consent:', error);
      setTimeout(() => initializeMixpanel(), 2000);
    }
  };
  initAfterConsent();
} else {
  // If Transcend not available, initialize after delay
  setTimeout(() => initializeMixpanel(), 2000);
}

export default function App({
  Component,
  pageProps
}: AppProps<{ session: Session | null }>) {
  useSOCRadar();

  return (
    <AuthProvider {...pageProps}>
      <UserProvider {...pageProps}>
        <PageDataProvider {...pageProps}>
          <OptimizelyProvider {...pageProps}>
            <SubscriptionsProvider>
              <ThemeProvider theme={GreenlightTheme}>
                <Global
                  styles={css`
                    ${glNormalize(GreenlightTheme)}
                  `}
                />
                <Script id="optimizely-inline" strategy="afterInteractive">
                  {GreenlightScript}
                </Script>
                <Script
                  id="bright-edge"
                  src="https://cdn.bc0a.com/autopilot/f00000000295792/autopilot_sdk.js"
                  strategy="afterInteractive"
                />
                <Script
                  id="optimizely-cdn"
                  src="https://cdn-pci.optimizely.com/js/16064820140.js"
                  strategy="afterInteractive"
                />
                <Script
                  id="outbrain-bootstrap"
                  strategy="afterInteractive"
                  data-obct
                >
                  {`/** DO NOT MODIFY THIS CODE**/
!function(_window, _document) {
  var OB_ADV_ID = '000c41803d2459e753521bcbb44cfe44d5';
  if (_window.obApi) {
    var toArray = function(object) {
      return Object.prototype.toString.call(object) === '[object Array]' ? object : [object];
    };
    _window.obApi.marketerId = toArray(_window.obApi.marketerId).concat(toArray(OB_ADV_ID));
    return;
  }
  var api = _window.obApi = function() {
    api.dispatch ? api.dispatch.apply(api, arguments) : api.queue.push(arguments);
  };
  api.version = '1.1';
  api.loaded = true;
  api.marketerId = OB_ADV_ID;
  api.queue = [];
  var tag = _document.createElement('script');
  tag.async = true;
  tag.src = '//amplify.outbrain.com/cp/obtp.js';
  tag.type = 'text/javascript';
  var script = _document.getElementsByTagName('script')[0];
  script.parentNode.insertBefore(tag, script);
}(window, document);

obApi('track', 'PAGE_VIEW');`}
                </Script>
                <Component {...pageProps} />
              </ThemeProvider>
            </SubscriptionsProvider>
          </OptimizelyProvider>
        </PageDataProvider>
      </UserProvider>
    </AuthProvider>
  );
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
  // Don't report the render or route change to render metrics
  if (
    !['Next.js-render', 'Next.js-route-change-to-render'].includes(metric.name)
  ) {
    addRumContext(metric.name, metric);
  }
}
